import { useState, useEffect } from "react";
import { reportApi } from "@/services/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Download, Search } from "lucide-react";
import ReportLayout from "@/components/reports/ReportLayout";
import TablePagination from "@/components/masters/TablePagination";
import { exportToExcel, ExcelColumn } from "@/utils/exportUtils";

interface CollectionService {
  serviceCode: string;
  serviceName: string;
  totalAmount: number;
  amountReceived: number;
  collectionName: string;
  collectionCount: number;
  billingCycle: string;
}

interface FetchParams {
  page: number;
  limit: number;
  query?: string;
}

const CollectionServiceReport = () => {
  const [data, setData] = useState<CollectionService[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalDocs, setTotalDocs] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    query: "",
  });
  const [allTotals, setAllTotals] = useState<{
    totalAmount: number;
    amountReceived: number;
  }>({ totalAmount: 0, amountReceived: 0 });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [currentPage, itemsPerPage, filters]);

  useEffect(() => {
    // Fetch all data for totals
    const fetchAllTotals = async () => {
      try {
        const params: FetchParams = {
          page: 1,
          limit: "all" as unknown as number,
        };
        if (filters.query) params.query = filters.query.trim();
        const response = await reportApi.getCollectionServiceReport(params);
        const docs = response.data?.docs || [];
        let totalAmount = 0;
        let totalPaid = 0;
        docs.forEach((item: CollectionService) => {
          totalAmount += item.totalAmount;
          totalPaid += item.amountReceived;
        });
        setAllTotals({ totalAmount, amountReceived: totalPaid });
      } catch {
        setAllTotals({ totalAmount: 0, amountReceived: 0 });
      }
    };
    fetchAllTotals();
    // eslint-disable-next-line
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params: FetchParams = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (filters.query) params.query = filters.query.trim();
      const response = await reportApi.getCollectionServiceReport(params);
      if (response && response.data && response.data.docs) {
        setData(response.data.docs);
        setTotalDocs(response.data.totalDocs);
        setHasNextPage(response.data.hasNextPage);
      }
    } catch (error: unknown) {
      const err = error as { message?: string };
      toast({
        title: "Error",
        description: err.message ?? "Failed to fetch report data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (
    filterName: keyof typeof filters,
    value: string
  ) => {
    setFilters({
      ...filters,
      [filterName]: value,
    });
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      query: "",
    });
    setCurrentPage(1);
    fetchData();
  };

  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      toast({
        title: "Exporting",
        description: "Preparing your Excel file...",
      });
      const exportParams: FetchParams = {
        page: 1,
        limit: "all" as unknown as number,
      };
      if (filters.query) exportParams.query = filters.query.trim();
      const response = await reportApi.getCollectionServiceReport(exportParams);
      const dataToExport = response.data?.docs || [];
      const columns: ExcelColumn[] = [
        { header: "SL No", key: "slNo", width: 10 },
        { header: "Service Code", key: "serviceCode", width: 15 },
        { header: "Service Name", key: "serviceName", width: 25 },
        { header: "Collection Name", key: "collectionName", width: 25 },
        { header: "Billing Cycle", key: "billingCycle", width: 15 },
        { header: "Collection Count", key: "collectionCount", width: 15 },
        { header: "Total Amount", key: "totalAmount", width: 15 },
        { header: "Total Paid", key: "amountReceived", width: 15 },
      ];
      const formattedData = dataToExport.map(
        (item: CollectionService, index: number) => ({
          slNo: index + 1,
          serviceCode: item.serviceCode,
          serviceName: item.serviceName,
          collectionName: item.collectionName,
          billingCycle: item.billingCycle,
          collectionCount: item.collectionCount,
          totalAmount: item.totalAmount,
          amountReceived: item.amountReceived,
        })
      );
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const filename = `collection_service_report_${dateStr}.xlsx`;
      await exportToExcel(formattedData, {
        filename,
        sheetName: "Collection Service Report",
        columns,
        styleHeader: true,
        totalRow: {
          keys: [
            { key: "totalAmount", label: "Total Amount" },
            { key: "amountReceived", label: "Total Paid" },
          ],
          label: "Total",
          singleRow: true,
        },
      });
      toast({
        title: "Export Successful",
        description: "Your report has been exported to Excel.",
      });
    } catch (error: unknown) {
      const err = error as { message?: string };
      toast({
        title: "Export Failed",
        description: err.message || "Failed to export data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = data.reduce((sum, item) => sum + item.totalAmount, 0);
  const totalPaid = data.reduce((sum, item) => sum + item.amountReceived, 0);

  return (
    <ReportLayout
      title="Collection Service Report"
      description="View and analyze all collection service records"
    >
      <Card>
        <CardContent className="pt-0 pb-2">
          <div className="flex flex-col md:flex-row md:items-end gap-4 w-full">
            <div className="flex-1 min-w-0">
              <Label className="text-xs invisible">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by service code or name"
                  value={filters.query}
                  onChange={(e) => handleFilterChange("query", e.target.value)}
                  className="pl-8 w-full h-10 placeholder:text-m"
                />
              </div>
            </div>
            <div className="flex flex-row gap-2 md:gap-4 w-full md:w-auto justify-end">
              <Button variant="outline" onClick={resetFilters}>
                Reset
              </Button>
              <Button
                onClick={handleExportToExcel}
                disabled={loading || data.length === 0}
                className="whitespace-nowrap"
              >
                <Download className="mr-2 h-4 w-4" />
                Export to Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SL No</TableHead>
                <TableHead>Service Code</TableHead>
                <TableHead>Service Name</TableHead>
                <TableHead>Collection Name</TableHead>
                <TableHead>Billing Cycle</TableHead>
                <TableHead>Collection Count</TableHead>
                <TableHead className="text-right">Total Amount</TableHead>
                <TableHead className="text-right">Total Paid</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-6">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-6 text-muted-foreground"
                  >
                    {filters.query
                      ? "No records match your search criteria"
                      : "No records found."}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item, index) => (
                  <TableRow
                    key={item.serviceCode + item.collectionName + index}
                  >
                    <TableCell>
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </TableCell>
                    <TableCell>{item.serviceCode}</TableCell>
                    <TableCell>{item.serviceName}</TableCell>
                    <TableCell>{item.collectionName}</TableCell>
                    <TableCell>{item.billingCycle}</TableCell>
                    <TableCell>{item.collectionCount}</TableCell>
                    <TableCell className="text-right font-medium">
                      {item.totalAmount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {item.amountReceived.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
              {/* Total Row */}
              {!loading && data.length > 0 && (
                <TableRow className="font-bold bg-muted">
                  <TableCell colSpan={6}>Total</TableCell>
                  <TableCell className="text-right font-medium">
                    {allTotals.totalAmount.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {allTotals.amountReceived.toLocaleString()}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-2">
            <TablePagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalDocs / itemsPerPage)}
              onPageChange={setCurrentPage}
            />
          </div>
        </CardContent>
      </Card>
    </ReportLayout>
  );
};

export default CollectionServiceReport;
